import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import { filter, map, mergeMap, of, switchMap, tap } from 'rxjs'
import { getExportToastInstance } from '../../components/Export/ExportToastContent'
import { getForceReloadToastInstance } from '../../components/Toasts/ForceReloadToast'
import {
  getOrderToastContents,
  getOrderToastType
} from '../../components/Toasts/helpers'
import { wasHydrated } from '../../helpers/hydration'
import { OrdersUpdatedAction } from '../order/actions'
import { FetchExportSecuritiesAction } from '../securities/actions'
import {
  getSecurityStaticDataById,
  type HasNsKey as SecuritiesSlice
} from '../securities/selectors'
import { namespace as wsNamespace } from '../ws'
import { State as WsState } from '../ws/reducer'
import { getInitTime, hasError, isPending } from '../ws/selectors'
import { addToast, removeToast } from './actions'

type WsSlice = { [wsNamespace]: WsState }

/* Whether the last ws.connect skipped the pending toast, so connect-failure knows
   there is nothing on screen for its error toast to stack on. */
let pendingToastSuppressed = false

const WS_PENDING_TOAST_ID = 'ws-connection-pending'
const WS_ERROR_TOAST_ID = 'ws-connection-error'
const WS_FORCE_RELOAD_TOAST_ID = 'ws-force-reload'

const orderToastEpic: Epic<Action, Action, WsSlice & SecuritiesSlice> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<OrdersUpdatedAction>('order.ordersUpdated'),
    filter((action) => {
      const { tradeTime, submitTime } = action.payload
      const orderTime = tradeTime || submitTime
      return dayjs(orderTime).isAfter(getInitTime(state$.value))
    }),
    map((action) => {
      const { securityId, transactionId, status } = action.payload
      const selector = getSecurityStaticDataById(state$.value)
      const staticData = selector(securityId)
      return addToast({
        message: getOrderToastContents(
          action.payload,
          staticData?.boardLabel ?? ''
        ),
        options: {
          toastId: `${transactionId}-${dayjs().toString()}`,
          type: getOrderToastType(status),
          position: 'bottom-right',
          autoClose: false
        }
      })
    })
  )

const exportToastEpic: Epic = (action$) =>
  action$.pipe(
    ofType<FetchExportSecuritiesAction>('securities.fetchExportSecurities'),
    switchMap(() => {
      return of(
        addToast({
          message: getExportToastInstance(),
          options: {
            toastId: 'exportToast',
            type: 'info',
            position: 'top-center',
            autoClose: false
          }
        })
      )
    })
  )

/*
    toast.dismiss only affects mounted react-toastify instances, so we also
    dispatch removeToast to drop any not-yet-opened entries from redux state.
    Without this, a fast ws.connect-success arriving before the pending toast
    opens would leave the queued entry behind and surface it later.
*/
const wsConnectingToastEpic: Epic<Action, Action, WsSlice> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('ws.connect'),
    mergeMap(() => {
      toast.dismiss(WS_ERROR_TOAST_ID)
      /*
          A hydrated window already has content on screen, so this would only cover
          it. Windows that booted cold still announce, and a reconnect (error still
          set) announces everywhere.
      */
      const announce = !wasHydrated() || hasError(state$.value)
      pendingToastSuppressed = !announce
      return of(
        removeToast(WS_ERROR_TOAST_ID),
        ...(announce
          ? [
              addToast({
                message: 'Establishing connection to the server…',
                options: {
                  toastId: WS_PENDING_TOAST_ID,
                  type: 'info',
                  autoClose: false,
                  closeButton: false
                }
              })
            ]
          : [])
      )
    })
  )

const wsConnectSuccessToastEpic: Epic = (action$) =>
  action$.pipe(
    ofType('ws.connect-success'),
    mergeMap(() => {
      toast.dismiss(WS_PENDING_TOAST_ID)
      toast.dismiss(WS_ERROR_TOAST_ID)
      return of(
        removeToast(WS_PENDING_TOAST_ID),
        removeToast(WS_ERROR_TOAST_ID)
      )
    })
  )

const wsConnectFailureToastEpic: Epic<Action, Action, WsSlice> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('ws.connect-failure'),
    /* connect-failure leaves pending set, so !isPending normally means "the pending
       toast is up, don't stack on it" — unless this attempt suppressed it, which
       leaves the error toast as the only signal. */
    filter(() => !isPending(state$.value) || pendingToastSuppressed),
    mergeMap(() =>
      of(
        addToast({
          message:
            'The connection to the server was lost. Retrying in a few seconds…',
          options: {
            toastId: WS_ERROR_TOAST_ID,
            type: 'error',
            autoClose: false,
            closeButton: false
          }
        })
      )
    )
  )

const wsForceReloadToastEpic: Epic = (action$) =>
  action$.pipe(
    ofType('ws.force-reload'),
    map(() =>
      addToast({
        message: getForceReloadToastInstance(),
        options: {
          toastId: WS_FORCE_RELOAD_TOAST_ID,
          type: 'error',
          autoClose: false,
          closeButton: false
        }
      })
    )
  )

const cancelForceReloadToastEpic: Epic = (action$) =>
  action$.pipe(
    ofType('ws.cancel-force-reload'),
    tap(() => toast.dismiss(WS_FORCE_RELOAD_TOAST_ID)),
    map(() => removeToast(WS_FORCE_RELOAD_TOAST_ID))
  )

export default combineEpics(
  orderToastEpic,
  exportToastEpic,
  wsConnectingToastEpic,
  wsConnectSuccessToastEpic,
  wsConnectFailureToastEpic,
  wsForceReloadToastEpic,
  cancelForceReloadToastEpic
)
